import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, FileCheck, Scale, ClipboardList, Home, Handshake, Gavel, Banknote, Landmark, Clock } from "lucide-react";
import Layout from "@/components/site/Layout";
import PageHero from "@/components/site/PageHero";
import { OC_STONE_HOME_URL } from "@/lib/assets";

const STEPS = [
  {
    icon: ShieldCheck,
    title: "Determine How the Property Is Held",
    body: [
      "Before anything else, figure out how title is held, because it determines your entire path forward:",
    ],
    bullets: [
      "**Held in a revocable living trust** — the successor trustee can typically sell the property directly, without court involvement.",
      "**Held in joint tenancy or with a transfer-on-death deed** — ownership often passes outside of probate entirely.",
      "**Held solely in the deceased's name with a will, or no will at all** — the property must go through *probate*, and the steps below apply.",
    ],
  },
  {
    icon: FileCheck,
    title: "Open Probate and Get Appointed",
    body: [
      "If the home is subject to probate, someone — usually the named executor or a qualified heir — files a Petition for Probate with Orange County Superior Court. The court schedules a hearing, confirms the personal representative, and issues Letters Testamentary or Letters of Administration, which is the legal document granting authority to act on the estate's behalf, including selling real property.",
      "This stage typically takes 6–8 weeks before anything else can move forward.",
    ],
  },
  {
    icon: Scale,
    title: "Determine Your Authority Level",
    body: [
      "This is the single most important factor in how your sale will play out. When the court appoints you, it grants one of two types of authority under the Independent Administration of Estates Act (IAEA):",
    ],
    bullets: [
      "**Full Authority** — You can list, accept an offer, and close escrow much like a traditional sale, by filing a Notice of Proposed Action and giving heirs 15 days to object. No courtroom hearing required if no one objects.",
      "**Limited Authority** — Any accepted offer must go before the probate judge at a court confirmation hearing, where the sale is subject to public overbidding, and the price can't go below 90% of the probate referee's appraised value.",
    ],
    footer: "If you have a choice, requesting full authority from the court early in the case can save months later. Ask your probate attorney about this before your initial hearing.",
  },
  {
    icon: ClipboardList,
    title: "Get the Probate Referee Appraisal",
    body: [
      "The court appoints a probate referee to formally appraise the property as of the date of death. This appraisal sets the statutory price floor for limited-authority sales and is also used for estate tax and accounting purposes.",
    ],
  },
  {
    icon: Home,
    title: "Prepare and List the Property",
    body: [
      "Once you have authority to sell, the property can be prepared and marketed. A few things make probate listings different from a standard sale:",
    ],
    bullets: [
      "The listing must disclose that the sale is subject to probate (and, if applicable, to court confirmation and overbidding).",
      "Homes can typically be sold as-is, but known material defects must still be disclosed.",
      "Pricing strategy matters: under limited authority, you're legally restricted from accepting less than 90% of the referee's appraised value, so pricing too low can create problems at confirmation.",
    ],
    footer: "Working with a probate-experienced agent here matters — a generalist agent can unintentionally violate notice or pricing rules in ways that delay or unwind a sale.",
  },
  {
    icon: Handshake,
    title: "Accept an Offer",
    body: [
      "Once you have an acceptable offer, what happens next depends on your authority level:",
    ],
    bullets: [
      "**Full Authority:** File the Notice of Proposed Action and move toward closing if no heir objects within 15 days.",
      "**Limited Authority:** File a Report of Sale and Petition for Order Confirming Sale of Real Property with the court and prepare for a confirmation hearing, typically scheduled 30–45 days out.",
    ],
  },
  {
    icon: Gavel,
    title: "The Court Confirmation Hearing (Limited Authority Only)",
    body: [
      "If your sale requires confirmation, this is the step that surprises most families and buyers. At the hearing:",
    ],
    bullets: [
      "The judge announces the accepted offer and opens the floor to overbids from anyone present.",
      "California sets the minimum first overbid by statute: 10% of the first $10,000 of the accepted price, plus 5% of the remaining balance, added to the original offer.",
      "Overbidders must typically bring a cashier's check (often 10% of their bid) to participate on the spot.",
      "The judge confirms the sale to whoever has the highest bid once bidding stops — which may or may not be your original buyer.",
    ],
    footer: "While this adds time and uncertainty, it exists to protect the estate: more bidders generally means a higher final sale price for the heirs.",
  },
  {
    icon: Landmark,
    title: "Close Escrow",
    body: [
      "Once the sale is confirmed — by the court (limited authority) or by clearing the objection period (full authority) — escrow proceeds much like a conventional transaction: title clears, the buyer's funds are deposited, and the personal representative executes the deed transferring the property.",
    ],
  },
  {
    icon: Banknote,
    title: "Apply Proceeds to the Estate",
    body: [
      "Sale proceeds don't go straight to the heirs. They first become part of the estate, used to pay any remaining debts, taxes, court costs, statutory fees, and carrying costs accrued during probate. What's left is distributed according to the will or, if there isn't one, California's intestate succession laws — through the estate's final accounting and distribution process.",
    ],
  },
];

const TIMELINE = [
  { scenario: "Trust sale (no probate)", timeline: "30–45 days" },
  { scenario: "Probate sale, full authority", timeline: "30–60 days once authority is granted" },
  { scenario: "Probate sale, limited authority (court confirmation required)", timeline: "Adds 2–4 months beyond a standard sale" },
  { scenario: "Full probate case to final closure", timeline: "9–18 months (often longer with disputes or complex assets)" },
];

const FAQS = [
  { q: "Can I sell a house before probate is fully completed?", a: "Yes. Once the court grants you authority — full or limited — you can list and sell the property; you don't need to wait for the entire estate to close." },
  { q: "Do I have to accept the original buyer's offer if there's a court confirmation hearing?", a: "The court ultimately confirms the sale to the highest bidder at the hearing, which is often but not always the original buyer. If overbid, the original buyer's deposit is returned." },
  { q: "Will I owe capital gains tax on an inherited home?", a: "Most heirs owe little to no capital gains tax on a prompt sale because of the federal step-up in basis, which resets the property's cost basis to its value at the date of death. Appreciation after that date may be taxable. (Consult a tax professional for your specific situation.)" },
  { q: "Can I sell the home as-is?", a: "Generally yes — probate sales are commonly sold as-is — but you're still required to disclose known material defects to buyers." },
  { q: "What's the benefit of full authority over limited authority?", a: "Full authority lets you avoid the court confirmation hearing and overbid process entirely (absent an heir objection), which typically saves months and reduces uncertainty for your buyer." },
];

function Rich({ text }) {
  // Simple bold/italic markdown → HTML
  const html = text
    .replace(/\*\*(.+?)\*\*/g, '<strong class="text-navy-900">$1</strong>')
    .replace(/\*(.+?)\*/g, '<em class="italic">$1</em>');
  return <span dangerouslySetInnerHTML={{ __html: html }} />;
}

export default function StepsToSellInheritedHome() {
  return (
    <Layout>
      <PageHero
        breadcrumb="Home / Probates / Steps to Sell an Inherited Home"
        overline="A Step-By-Step Guide"
        title="Steps to Successfully Selling an"
        accentWord="Inherited Home."
        description={"Selling a home you've inherited isn't like a typical Orange County real estate transaction — especially if the property is going through probate. Between court authority levels, statutory pricing rules, and potential courtroom overbidding, there's a specific sequence you need to follow to protect the estate's value and avoid costly delays."}
        image="https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1600&q=80"
        rightImage={OC_STONE_HOME_URL}
        rightImageAlt="Beautifully landscaped Orange County home"
      />

      {/* Steps */}
      <section className="py-16 bg-white" data-testid="steps-list">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 space-y-6">
          {STEPS.map((s, i) => (
            <motion.article
              key={s.title}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.05 }}
              data-testid={`step-${i}`}
              className="rounded-3xl border border-navy-100 bg-white hover:border-gold-400 hover:shadow-xl transition-all grid md:grid-cols-12 gap-6 p-7 sm:p-9"
            >
              <div className="md:col-span-2 flex md:flex-col items-start gap-4">
                <div className="font-heading text-4xl text-gold-500">{String(i + 1).padStart(2, "0")}</div>
                <div className="h-12 w-12 rounded-xl bg-navy-900 text-gold-400 flex items-center justify-center">
                  <s.icon size={20} />
                </div>
              </div>
              <div className="md:col-span-10">
                <div className="text-[10px] uppercase tracking-[0.24em] text-gold-700 font-semibold">Step {i + 1}</div>
                <h3 className="mt-1.5 font-heading text-2xl sm:text-3xl text-navy-900 leading-tight">{s.title}</h3>
                <div className="mt-4 space-y-3 text-base text-navy-700 leading-relaxed">
                  {s.body.map((p, pi) => (
                    <p key={pi}><Rich text={p} /></p>
                  ))}
                </div>
                {s.bullets && (
                  <ul className="mt-4 space-y-2.5">
                    {s.bullets.map((b, bi) => (
                      <li key={bi} className="flex items-start gap-2.5 text-base text-navy-700 leading-relaxed">
                        <span className="mt-2 h-1.5 w-1.5 rounded-full bg-gold-500 shrink-0" />
                        <span><Rich text={b} /></span>
                      </li>
                    ))}
                  </ul>
                )}
                {s.footer && (
                  <div className="mt-4 rounded-xl bg-gold-500/5 border border-gold-500/30 p-4 text-base text-navy-700 italic">
                    <Rich text={s.footer} />
                  </div>
                )}
              </div>
            </motion.article>
          ))}
        </div>
      </section>

      {/* Timeline table */}
      <section className="py-20 bg-navy-50/40" data-testid="timeline-table">
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-start gap-4 mb-8">
            <Clock size={32} className="text-gold-600 shrink-0 mt-1" />
            <div>
              <span className="section-overline">How Long Does It Take?</span>
              <h2 className="mt-2 font-heading text-3xl sm:text-4xl text-navy-900 leading-tight">
                A realistic look at <em className="italic text-gold-600">timelines</em>.
              </h2>
            </div>
          </div>
          <div className="rounded-3xl overflow-hidden border border-navy-100 bg-white">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-navy-900 text-white text-left">
                  <th className="px-6 py-4 font-heading text-base">Scenario</th>
                  <th className="px-6 py-4 font-heading text-base">Typical Timeline</th>
                </tr>
              </thead>
              <tbody>
                {TIMELINE.map((row, i) => (
                  <tr key={i} className={`${i % 2 ? "bg-navy-50/40" : "bg-white"} border-t border-navy-100`} data-testid={`timeline-row-${i}`}>
                    <td className="px-6 py-4 text-navy-800 font-medium">{row.scenario}</td>
                    <td className="px-6 py-4 text-navy-700">{row.timeline}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-4 text-xs text-navy-500 italic">Note: the home itself can often sell well before the overall probate case is fully closed.</p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 bg-white" data-testid="steps-faqs">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <span className="section-overline">FAQs · Selling an Inherited House in Orange County</span>
          <h2 className="mt-3 font-heading text-4xl text-navy-900 leading-tight">
            Straight answers about the <em className="italic text-gold-600">selling process</em>.
          </h2>
          <div className="mt-10 divide-y divide-navy-200/60 border-y border-navy-200/60">
            {FAQS.map((f, i) => (
              <details key={i} className="group py-5" data-testid={`steps-faq-${i}`}>
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <h3 className="font-heading text-lg text-navy-900 pr-6">{f.q}</h3>
                  <span className="h-8 w-8 rounded-full border border-navy-200 flex items-center justify-center text-navy-700 group-open:bg-gold-500 group-open:text-navy-900 group-open:border-gold-500 transition-all">
                    +
                  </span>
                </summary>
                <p className="mt-3 text-navy-600 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-navy-900 relative overflow-hidden">
        <div className="absolute -top-32 -right-32 h-96 w-96 rounded-full bg-gold-500/15 blur-3xl" />
        <div className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8 relative text-center">
          <h2 className="font-heading text-4xl sm:text-5xl text-white leading-tight">
            Selling your inherited Orange County property <em className="italic text-gold-400">the right way</em>.
          </h2>
          <p className="mt-5 text-white/70 max-w-3xl mx-auto">
            Every one of these steps has a procedural detail that can delay your sale or reduce what the estate ultimately receives — from getting the right authority type to pricing correctly against the probate referee's appraisal to being prepared for an overbid hearing. A probate real estate specialist works alongside your attorney to keep each step on track and help the estate net the strongest possible outcome.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/contact" data-testid="steps-cta-contact" className="btn-gold group">
              Schedule a Free Consultation
              <ArrowRight size={16} className="ml-2 transition-transform group-hover:translate-x-1" />
            </Link>
            <a href="tel:+17143913645" data-testid="steps-cta-phone" className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 text-white px-6 py-3 text-sm font-medium hover:bg-white hover:text-navy-900 transition-all">
              (714) 391-3645
            </a>
          </div>
          <p className="mt-8 text-xs text-white/50 max-w-3xl mx-auto italic">
            This page is for general informational purposes and does not constitute legal or tax advice. Personal representatives should consult a licensed California probate attorney, and heirs should consult a tax professional, regarding their specific situation.
          </p>
        </div>
      </section>
    </Layout>
  );
}
